import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { lastValueFrom } from 'rxjs';
import { AccountService } from 'src/app/core/services/account.service';
import { MemoryStorageService } from 'src/app/core/services/memory-storage.service';
import { OneFiService } from 'src/app/core/services/onefi.service';
import swal from 'sweetalert2';

@Component({
  selector: 'app-onefi-application',
  templateUrl: './application.component.html',
  styleUrls: ['./application.component.scss']
})
export class ApplicationComponent implements OnInit, OnDestroy {
  isLoading: boolean = true;
  hasError: boolean = false;
  errorMessage: string = '';
  redirectUrl: string = '';
  transactionId: string = '';

  constructor(
    private oneFiService: OneFiService,
    private accountService: AccountService,
    private router: Router,
    private spinner: NgxSpinnerService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    this.spinner.hide();
    this.initiateRedirect();
  }

  ngOnDestroy(): void {
    this.spinner.hide();
  }

  async initiateRedirect(): Promise<void> {
    this.isLoading = true;
    this.hasError = false;
    this.errorMessage = '';
    this.cdr.detectChanges();

    try {
      // 1. Check existing storage / memory for hosted URL
      let targetUrl = MemoryStorageService.getItem('oneFiRedirectUrl') 
        || MemoryStorageService.getItem('redirectUrl')
        || localStorage.getItem('oneFiPaymentUrl');

      let txId = MemoryStorageService.getItem('oneFiTransactionId')
        || MemoryStorageService.getItem('lenderApplId')
        || localStorage.getItem('oneFiTransactionId');

      // 2. Fallback to Navigator if not found
      if (!targetUrl || !txId) {
        try {
          const navRes: any = await lastValueFrom(this.accountService.getNavigator());
          if (navRes?.data?.applicationDetail?.redirectUrl) {
            targetUrl = navRes.data.applicationDetail.redirectUrl;
          }
          if (navRes?.data?.applicationDetail?.lenderApplId) {
            txId = navRes.data.applicationDetail.lenderApplId;
          }
        } catch (navErr) {
          console.warn('Could not fetch navigator for 1Fi redirect:', navErr);
        }
      }

      // 3. If targetUrl still missing, generate fresh hosted transaction
      if (!targetUrl) {
        const hostedRes: any = await lastValueFrom(this.oneFiService.createHostedTransaction({}));
        if (hostedRes?.data?.payment_url) {
          targetUrl = hostedRes.data.payment_url;
          if (hostedRes.data.transaction_id) {
            txId = hostedRes.data.transaction_id;
          }
        } else if (hostedRes?.error) {
          throw new Error(hostedRes.error);
        }
      }

      if (targetUrl) {
        this.redirectUrl = targetUrl;
        this.transactionId = txId || '';

        MemoryStorageService.setItem('oneFiRedirectUrl', targetUrl);
        MemoryStorageService.setItem('redirectUrl', targetUrl);
        localStorage.setItem('oneFiPaymentUrl', targetUrl);

        if (this.transactionId) {
          MemoryStorageService.setItem('oneFiTransactionId', this.transactionId);
          MemoryStorageService.setItem('lenderApplId', this.transactionId);
          localStorage.setItem('oneFiTransactionId', this.transactionId);
        }

        this.isLoading = false;
        this.cdr.detectChanges();

        // Redirect immediately in the SAME tab (NO new tab)
        this.redirectNow();
      } else {
        throw new Error('Unable to obtain the 1Fi redirection URL. Please try again.');
      }
    } catch (error: any) {
      console.error('Error during 1Fi redirect:', error);
      this.ngZone.run(() => {
        this.isLoading = false;
        this.hasError = true;
        this.errorMessage = error?.message || 'Failed to connect to 1Fi. Please try again.';
        this.cdr.detectChanges();
      });
      swal.fire('Error', this.errorMessage, 'error');
    }
  }

  redirectNow(): void {
    if (this.redirectUrl) {
      window.location.href = this.redirectUrl;
    }
  }

  retry(): void {
    this.initiateRedirect();
  }
}
